import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { Button, Box, Container, Typography } from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons'

import { filter, includes } from 'lodash'

import { ConfirmationDialog } from '@scoir/modals'
import ComponentLoader from '@scoir/components-core/dist/components/ComponentLoader'
import Table from '@scoir/components-table/dist/Table'
import update from '@scoir/immutability'

import api from 'common/api'
import { formatter } from 'common/date'
import permissions from 'common/system-permissions'

import InviteUsersModal from './InviteUsersModal'
import ActionsCell from './ActionsCell'
import ActiveCell from './ActiveCell'
import ManageUserModal from './ManageUserModal'
import ManageRolesModal from './ManageRolesModal'

const DEFAULT_RESULTS = {
  Total: 0,
  Users: [],
}

export class ManageUsers extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      error: false,
      loaded: false,
      openEdit: false,
      openDelete: false,
      openRoles: false,
      editingRolesUser: {},
      results: DEFAULT_RESULTS,
    }
  }

  componentDidMount() {
    document.title = 'Scoir - Manage Users'
  }

  fetch({ from, pageSize, sortKey, sortDir, filter }) {
    const sort = sortDir === 'asc' ? sortKey : `-${sortKey}`
    api
      .get(
        `/api/college/${this.props.scid}/admissions_officers?pageSize=${pageSize}&from=${from}&sort=${sort}&search=${filter}`,
      )
      .then((results) =>
        this.setState({
          loaded: true,
          results: {
            ...results,
            Users: results.Users || DEFAULT_RESULTS.Users,
          },
        }),
      )
      .catch(() =>
        this.setState({
          error: true,
          loaded: true,
          results: DEFAULT_RESULTS,
        }),
      )
  }

  saveUser(user, opts) {
    let prom
    if (user.Id) {
      prom = api.put(`/api/college/${this.props.scid}/admissions_officers/${user.Id}`, user)
    } else {
      prom = api
        .post(`/api/users?invite=${opts.inviteNow}&type=AdmissionsOfficer`, {
          ...user,
          Scid: this.props.scid,
        })
        .then((newUser) => {
          this.setState({ openRoles: true, editingRolesUser: newUser })
        })
    }
    return prom
      .then(() => this.setState({ openEdit: false, editingUser: undefined }))
      .then(() => this.fetch(this.state.tableContext))
  }

  inviteStaff(userIds) {
    api.post('/api/invites', userIds).then(() => this.fetch(this.state.tableContext))
  }

  toggleItem(idx) {
    const results = update(this.state.results, {
      Users: {
        [idx]: {
          $toggle: ['selected'],
        },
      },
    })
    this.setState({ results })
  }

  toggleAll(active) {
    const results = update(this.state.results, {
      Users: {
        $each: {
          selected: {
            $set: active,
          },
        },
      },
    })
    this.setState({ results })
  }

  onDeleteUser() {
    api.delete(`/api/college/${this.props.scid}/admissions_officers/${this.state.deleteUser.Id}`).then(() => {
      this.setState({ openDelete: false })
      return this.fetch(this.state.tableContext)
    })
  }
  closeRolesModal(hasChanges) {
    this.setState({ openRoles: false })
    if (hasChanges) {
      this.fetch(this.state.tableContext)
    }
  }
  isUserPermitted() {
    return includes(this.props.userPermissions, permissions.MANAGE_ADMISSIONS)
  }

  render() {
    const isUserPermitted = this.isUserPermitted()
    const tableActions = isUserPermitted
      ? [
          {
            label: 'Invite Staff',
            onSelect: () => this.setState({ openInvite: true }),
          },
        ]
      : undefined
    const controlPanel = isUserPermitted ? (
      <Button
        variant="outlined"
        onClick={() => this.setState({ openEdit: true, editingUser: undefined })}
      >
        <FontAwesomeIcon
          size="xs"
          icon={faPlusCircle}
        />{' '}
        &nbsp; Add New User
      </Button>
    ) : null
    return (
      <Container
        className="route-manage-users"
        maxWidth={false}
      >
        <ComponentLoader loaded={this.state.loaded}>
          <Box
            borderRadius="borderRadius"
            bgcolor="background.paper"
            p={{ xs: 1, sm: 3 }}
            mt={{ xs: 1, sm: 2 }}
          >
            <Table
              total={this.state.results.Total}
              records={this.state.results.Users}
              keyedBy="Id"
              canFilter={true}
              canChangePageSize={true}
              defaultSortKey="LastName"
              defaultSortDir="asc"
              onTableChange={(context) => {
                this.setState({
                  tableContext: context,
                })
                this.fetch(context)
              }}
              actions={tableActions}
              controlPanel={controlPanel}
              onRecordToggle={(idx) => this.toggleItem(idx)}
              onToggleAll={(active) => this.toggleAll(active)}
              columnDefs={[
                {
                  headerText: 'Last',
                  sortKey: 'LastName',
                  renderCell: (record) => record.LastName,
                },
                {
                  headerText: 'First',
                  sortKey: 'FirstName',
                  renderCell: (record) => record.FirstName,
                },
                {
                  headerText: 'Title',
                  sortKey: 'Title',
                  renderCell: (record) => record.Title,
                },
                {
                  headerText: 'Status',
                  sortKey: 'Active',
                  renderCell: (record) => <ActiveCell record={record} />,
                },
                {
                  headerText: 'Last Login',
                  sortKey: 'LastLoginTime',
                  renderCell: (record) =>
                    formatter.format(record.LastLoginTime, {
                      outputFormat: 'MMM DD, YYYY h:mm:ss a',
                    }) || <Fragment>&mdash;</Fragment>,
                },
                {
                  renderCell: (record) => (
                    <ActionsCell
                      isUserPermitted={isUserPermitted}
                      record={record}
                      onEdit={() =>
                        this.setState({
                          openEdit: true,
                          editingUser: record,
                        })
                      }
                      onDelete={() =>
                        this.setState({
                          openDelete: true,
                          deleteUser: record,
                        })
                      }
                      onRolesEdit={() =>
                        this.setState({
                          openRoles: true,
                          editingRolesUser: record,
                        })
                      }
                    />
                  ),
                },
              ]}
            />

            <ManageUserModal
              open={this.state.openEdit}
              user={this.state.editingUser}
              isNew={this.state.editingUser === undefined}
              onClose={() => this.setState({ openEdit: false })}
              onSubmitChanges={(user, opts) => this.saveUser(user, opts)}
            />
            <ConfirmationDialog
              open={this.state.openDelete}
              headerText="Confirm delete"
              onClose={() => this.setState({ openDelete: false })}
              onConfirm={() => this.onDeleteUser()}
            >
              <Typography>Are you sure you want to delete this staff member?</Typography>
              {this.state.deleteUser && (
                <Typography>
                  <strong>{this.state.deleteUser.FirstName + ' ' + this.state.deleteUser.LastName}</strong>
                </Typography>
              )}
            </ConfirmationDialog>
            <InviteUsersModal
              open={this.state.openInvite}
              onClose={() => this.setState({ openInvite: false })}
              users={filter(this.state.results.Users, { selected: true })}
              onSubmit={(userIds) => this.inviteStaff(userIds)}
            />
            <ManageRolesModal
              isUserPermitted={isUserPermitted && this.props.userId !== this.state.editingRolesUser.Id}
              open={this.state.openRoles}
              onClose={(hasChanges) => this.closeRolesModal(hasChanges)}
              userTarget={this.state.editingRolesUser}
            />
          </Box>
        </ComponentLoader>
      </Container>
    )
  }
}

ManageUsers.propTypes = {
  scid: PropTypes.number.isRequired,
  userPermissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  userId: PropTypes.string.isRequired,
}

export default connect((store) => ({
  scid: store.user.record.Scid,
  userPermissions: store.user.record.Permissions,
  userId: store.user.record.Id,
}))(ManageUsers)
